import type { Grade } from '../../shared/contract'
import { GRADES, isGrade, isStatus, type Filter, type Status } from './tally'

/**
 * What the reader has chosen: an opened area and the filter. Lives in the URL
 * (open, status, grade) so a view can be shared or reloaded. Each tap replaces
 * the entry rather than adding history, except opening an area on a phone:
 * that adds one, so Back closes the area instead of leaving the page.
 */
export class View {
  open = $state<string | null>(null)
  status = $state<Status | null>(null)
  grades = $state<Grade[]>([])
  /** The next sync adds a history entry. */
  #push = false
  /** The current entry is one this view added for an opened area. */
  #entry = false

  constructor(search = typeof location === 'undefined' ? '' : location.search) {
    const q = new URLSearchParams(search)
    this.open = q.get('open') || null
    const status = q.get('status')
    this.status = isStatus(status) ? status : null
    this.grades = GRADES.filter((g) => (q.get('grade') ?? '').split(',').includes(g))
  }

  get filter(): Filter {
    return { status: this.status, grades: this.grades }
  }

  setStatus(status: Status | null) {
    this.status = this.status === status ? null : status
  }

  toggleGrade(grade: Grade) {
    const on = this.grades.includes(grade)
    this.grades = GRADES.filter((g) => (g === grade ? !on : this.grades.includes(g)))
  }

  clear() {
    this.status = null
    this.grades = []
  }

  /** Opens or closes an area; `push` (phones) gives an opening its own history entry. */
  toggleArea(name: string, push = false) {
    if (this.open === name) this.close()
    else {
      if (push && !this.open) this.#push = true
      this.open = name
    }
  }

  /** Closes the opened area, going back over the entry its opening added. */
  close() {
    if (!this.open) return
    this.open = null
    this.#push = false
    if (this.#entry) {
      this.#entry = false
      history.back()
    }
  }

  /** Back or Forward: the area follows the URL; the filter stays as it is and is written back. */
  popstate() {
    this.#entry = false
    this.#push = false
    this.open = new URLSearchParams(location.search).get('open') || null
    this.sync()
  }

  /** The URL for this view, keeping any other parameters as they are. */
  search(current: string): string {
    const q = new URLSearchParams(current)
    const set = (key: string, value: string | null) => (value ? q.set(key, value) : q.delete(key))
    set('open', this.open)
    set('status', this.status)
    set('grade', this.grades.filter(isGrade).join(',') || null)
    const s = q.toString().replace(/\+/g, '%20').replace(/%2C/gi, ',')
    return s ? `?${s}` : ''
  }

  sync() {
    const next = this.search(location.search)
    if (this.#push) {
      this.#push = false
      this.#entry = true
      history.pushState(history.state, '', next || location.pathname)
    } else if (next !== location.search)
      history.replaceState(history.state, '', next || location.pathname)
  }
}
